import * as React from 'react';
import styled from '@emotion/styled';

import { Switch, Tooltip, Text, tokens } from '@hotjar/hotsauce';
import { noop } from '@hotjar/webapp/lib/function';
import { ErrorBoundary } from '@hotjar/error-handler';

const FeedbackEnablementContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const InputContainer = styled.div`
  margin: ${tokens.spacer.x1} 0;
`;

const FixedWidthInputContainer = styled(InputContainer)`
  width: 320px;
`;

const SwitchWithNoMargin = styled(Switch)`
  // NOTE: HotSauce doesn't define margin for the Switch component but under the hood it's a <label> and some old CSS
  // with very high specificity does
  margin-bottom: 0;
`;

interface ForwardOptionProps {
  className?: string;
  header: React.ReactNode;
  subtext?: React.ReactNode;
  input: React.ReactNode;
  isEnabled: boolean;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onChange: (isChecked: boolean) => void;
  switchDisabled?: boolean;
  switchTooltipContent?: string;
  isFixedWidth?: boolean;
}

export const ForwardOption = ({
  className,
  header,
  input,
  subtext,
  isEnabled,
  onClick = noop,
  onChange,
  switchDisabled = false,
  switchTooltipContent = '',
  isFixedWidth = true,
}: ForwardOptionProps): JSX.Element => {
  const Container = isFixedWidth ? FixedWidthInputContainer : InputContainer;

  return (
    <ErrorBoundary squadSentryTag="integrations">
      <article className={className}>
        <FeedbackEnablementContainer>
          {header}
          <Tooltip placement="top" text={switchTooltipContent} isDisabled={!switchTooltipContent}>
            {/* Disabling the a11y checks here since clicking on the switch when its disabled shows an upsell. Our Switch
           doesn't support this so this click handler is just there in case a user thinks to click. Its not really meant
           to be keyboard discoverable like other elements */}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <span onClick={onClick}>
              <SwitchWithNoMargin
                isChecked={isEnabled}
                onChange={onChange}
                size="s"
                isDisabled={switchDisabled}
              />
            </span>
          </Tooltip>
        </FeedbackEnablementContainer>
        {isEnabled && (
          <div>
            <Container>{input}</Container>
            {subtext && (
              <Text as="span" textType="caption" textColor="text_light">
                {subtext}
              </Text>
            )}
          </div>
        )}
      </article>
    </ErrorBoundary>
  );
};
